import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CloseIcon } from '@icons/CloseIcon';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ImagePlaceholderIcon, EmptySkuIcon } from '../icons';
import { ArrowUpIcon } from '@icons/ArrowUpIcon';
import { useTranslation } from '@hooks/useTranslation';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Dropzone } from '@/components/ui/dropzone';
import type { EditableImage } from '@hooks/useImageEditor';
import { UploadImagesModal } from './UploadImagesModal';

const MAX_IMAGES = 4;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

/** Creates stable blob URLs for File objects and revokes them on cleanup to prevent memory leaks. */
function useObjectUrls(files: File[]): string[] {
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);

  useEffect(() => {
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [urls]);

  return urls;
}

interface DropzoneUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingCount?: number;
  hidePreview?: boolean;
}

interface EditUploadProps {
  existingImages: EditableImage[];
  newFiles: File[];
  onNewFilesChange: (files: File[]) => void;
  onRemoveExisting: (signedId: string) => void;
}

function validateFiles(files: File[], currentCount: number): { valid: File[]; errors: string[] } {
  const errors: string[] = [];
  const valid: File[] = [];
  const remaining = MAX_IMAGES - currentCount;

  for (const file of files) {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      errors.push(`${file.name}: must be JPG or PNG`);
      continue;
    }
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`${file.name}: must be less than 5MB`);
      continue;
    }
    if (valid.length >= remaining) {
      errors.push(`Maximum ${MAX_IMAGES} images allowed`);
      break;
    }
    valid.push(file);
  }

  return { valid, errors };
}

export function DropzoneUpload({ files, onFilesChange, existingCount = 0, hidePreview = false }: DropzoneUploadProps) {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(files.length);
  const [dropzoneTooltipOpen, setDropzoneTooltipOpen] = useState(false);
  const fileUrls = useObjectUrls(files);

  const handleFiles = useCallback(
    (incoming: FileList) => {
      const { valid, errors } = validateFiles(Array.from(incoming), existingCount + files.length);
      if (errors.length > 0) {
        toast({ description: errors[0], variant: 'destructive', duration: 5000 });
      }
      if (valid.length > 0) {
        setVisibleCount(files.length);
        setUploadProgress(0);
        onFilesChange([...files, ...valid]);
      }
    },
    [files, onFilesChange, existingCount],
  );

  // Simulated upload progress when files are added
  useEffect(() => {
    if (uploadProgress == null) return;
    if (uploadProgress >= 100) {
      const timeout = setTimeout(() => {
        setUploadProgress(null);
        setVisibleCount(files.length);
      }, 500);
      return () => clearTimeout(timeout);
    }
    const timeout = setTimeout(() => setUploadProgress((prev) => Math.min((prev ?? 0) + 20, 100)), 150);
    return () => clearTimeout(timeout);
  }, [uploadProgress, files.length]);

  const removeFile = useCallback(
    (index: number) => {
      const updated = files.filter((_, i) => i !== index);
      setVisibleCount(updated.length);
      onFilesChange(updated);
    },
    [files, onFilesChange],
  );

  const isMaxReached = existingCount + files.length >= MAX_IMAGES;

  const dropzoneContent = (
    <Dropzone
      onFiles={handleFiles}
      accept="image/jpeg,image/png"
      multiple
      disabled={isMaxReached}
      className="h-[250px] gap-[18px]"
    >
      <ImagePlaceholderIcon aria-hidden="true" />
      <div className="flex flex-col items-center gap-3">
        <p className="font-inter text-sm font-semibold text-grey">
          {t('common.images.drag_and_drop')}
          <span className={UNDERLINE_STATIC}>{t('common.images.click_to_upload')}</span>
        </p>
        <p className="font-inter text-xs text-grey">{t('common.images.file_formats')}</p>
      </div>
    </Dropzone>
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Dropzone — disabled with tooltip when max images reached.
          Tooltip is controlled and the dropzone has pointer-events-none so
          clicking it doesn't dismiss the tooltip via Radix's pointerdown handler. */}
      {isMaxReached ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip open={dropzoneTooltipOpen}>
            <TooltipTrigger asChild>
              <span
                onMouseEnter={() => setDropzoneTooltipOpen(true)}
                onMouseLeave={() => setDropzoneTooltipOpen(false)}
              >
                {dropzoneContent}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t('common.images.max_images_tooltip')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        dropzoneContent
      )}

      {/* Upload progress bar */}
      {uploadProgress != null && (
        <div className="flex flex-col gap-2">
          <span className="font-inter text-sm text-grey">{t('common.images.uploading')}</span>
          <div className="h-1 overflow-hidden rounded-full bg-dinnerplate-white">
            <div
              className="h-full bg-brand-yellow transition-[width] duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Thumbnail previews — previous uploads always visible, new ones appear after progress */}
      {!hidePreview && visibleCount > 0 && (
        <div className="flex gap-3">
          {files.slice(0, visibleCount).map((file, index) => (
            <div key={`${file.name}-${index}`} className="relative">
              <div className="h-24 w-24 rounded-input overflow-hidden border border-dinnerplate-white bg-dinnerplate-white">
                <img
                  src={fileUrls[index]}
                  alt={file.name}
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeFile(index)}
                className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-dinnerplate-white text-grey"
              >
                <CloseIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function EditUpload({ existingImages, newFiles, onNewFilesChange, onRemoveExisting }: EditUploadProps) {
  const { t } = useTranslation();
  const totalCount = existingImages.length + newFiles.length;
  const newFileUrls = useObjectUrls(newFiles);

  const allThumbnails: ({ type: 'existing'; image: EditableImage } | { type: 'new'; file: File; index: number })[] = [
    ...existingImages.map((image) => ({ type: 'existing' as const, image })),
    ...newFiles.map((file, index) => ({ type: 'new' as const, file, index })),
  ];

  const [selectedIndex, setSelectedIndex] = useState(0);
  const activeIndex = Math.min(selectedIndex, allThumbnails.length - 1);
  const [uploadTooltipOpen, setUploadTooltipOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const mainImageSrc =
    allThumbnails.length > 0
      ? allThumbnails[activeIndex]?.type === 'existing'
        ? allThumbnails[activeIndex].image.url
        : newFileUrls[allThumbnails[activeIndex].index]
      : null;

  return (
    <div className="flex flex-col gap-4 shrink-0">
      {/* Main image area */}
      <div className="flex w-edit-image aspect-square items-center justify-center rounded-input bg-dinnerplate-white overflow-hidden">
        {mainImageSrc ? (
          <img src={mainImageSrc} alt="" className="h-full w-full object-contain" />
        ) : (
          <EmptySkuIcon className="w-24 h-20 text-grey" aria-hidden="true" />
        )}
      </div>

      {/* Thumbnail row */}
      <div className="flex gap-3 items-center">
        {allThumbnails.map((thumb, i) => {
          const src =
            thumb.type === 'existing'
              ? thumb.image.url
              : newFileUrls[thumb.index];

          return (
            <div key={thumb.type === 'existing' ? thumb.image.signed_id : `new-${thumb.index}`} className="relative group">
              <button
                type="button"
                onClick={() => setSelectedIndex(i)}
                className={cn(
                  'h-24 w-24 rounded-input overflow-hidden border bg-dinnerplate-white',
                  i === activeIndex ? 'border-grey' : 'border-transparent',
                )}
              >
                <img src={src} alt="" className="h-full w-full object-contain" />
              </button>
              <button
                type="button"
                aria-label="Remove image"
                onClick={() => {
                  if (thumb.type === 'existing') {
                    onRemoveExisting(thumb.image.signed_id);
                  } else {
                    onNewFilesChange(newFiles.filter((_, fi) => fi !== thumb.index));
                  }
                  if (activeIndex >= allThumbnails.length - 1) {
                    setSelectedIndex(Math.max(0, allThumbnails.length - 2));
                  }
                }}
                className="absolute -top-2 -right-2 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-dinnerplate-white text-grey"
              >
                <CloseIcon className="h-3 w-3" />
              </button>
            </div>
          );
        })}

        {totalCount >= MAX_IMAGES ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip open={uploadTooltipOpen}>
              <TooltipTrigger asChild>
                <span
                  onMouseEnter={() => setUploadTooltipOpen(true)}
                  onMouseLeave={() => setUploadTooltipOpen(false)}
                >
                  <button
                    type="button"
                    disabled
                    className="flex w-24 h-24 flex-col items-center justify-center gap-1 rounded-input border border-grey-light bg-dinnerplate-white-lighter font-inter text-xs font-semibold text-grey opacity-50 cursor-not-allowed pointer-events-none"
                  >
                    <ArrowUpIcon aria-hidden="true" />
                    {t('edit.upload')}
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t('common.images.max_images_tooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <button
            type="button"
            onClick={() => setIsUploadModalOpen(true)}
            className="flex w-24 h-24 flex-col items-center justify-center gap-1 rounded-input border border-grey-light bg-dinnerplate-white-lighter font-inter text-xs font-semibold text-grey"
          >
            <ArrowUpIcon aria-hidden="true" />
            {t('edit.upload')}
          </button>
        )}
      </div>

      <UploadImagesModal
        open={isUploadModalOpen}
        existingImages={existingImages}
        newFiles={newFiles}
        onOpenChange={setIsUploadModalOpen}
        onConfirm={(files) => onNewFilesChange([...newFiles, ...files])}
        onRemoveExisting={onRemoveExisting}
        onRemoveNewFile={(index) => onNewFilesChange(newFiles.filter((_, i) => i !== index))}
      />
    </div>
  );
}
